import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, getAccessToken, setAccessToken } from '../lib/api';
import { hasTelegramRuntime, telegram } from '../lib/telegram';
import type { User } from '../types';
import { Button, ErrorNotice, Input, LoadingScreen } from './ui';

export function SessionGate({ children }: { children: (user: User) => ReactNode }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; user: User }
    | { kind: 'local'; error?: unknown }
    | { kind: 'no-init-data' }
    | { kind: 'error'; error: unknown }
  >({ kind: 'loading' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void authenticate();
  }, []);

  async function authenticate(): Promise<void> {
    const stored = getAccessToken();
    if (stored) {
      try {
        const session = await api<{ user: User }>('/auth/session');
        setState({ kind: 'ready', user: session.user });
        return;
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) {
          setState({ kind: 'error', error });
          return;
        }
        setAccessToken(null);
      }
    }

    const initData = telegram()?.initData;
    if (!initData) {
      /*
       * SDK на странице есть, а подписанных данных нет — Mini App открыли
       * прямой ссылкой, а не кнопкой бота. Поле для токена тут ни при чём:
       * человеку нужно сказать, как открыть правильно.
       */
      setState({ kind: hasTelegramRuntime() ? 'no-init-data' : 'local' });
      return;
    }

    try {
      const session = await api<{ accessToken: string; user: User }>('/auth/telegram', {
        method: 'POST',
        body: { initData },
        auth: false,
      });
      setAccessToken(session.accessToken);
      setState({ kind: 'ready', user: session.user });
    } catch (error) {
      setState({ kind: 'error', error });
    }
  }

  if (state.kind === 'loading') return <LoadingScreen />;
  if (state.kind === 'ready') return children(state.user);
  if (state.kind === 'no-init-data') {
    return (
      <main className="center-page">
        <div className="brand-mark">AF</div>
        <h1>Откройте через кнопку бота</h1>
        <p className="muted">
          По прямой ссылке Telegram не передаёт данные для входа. Нажмите кнопку меню рядом с полем
          ввода в чате с ботом или отправьте ему /app.
        </p>
        <Button onClick={() => window.location.reload()}>Обновить</Button>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main className="center-page">
        <div className="brand-mark">AF</div>
        <h1>Не удалось войти</h1>
        <ErrorNotice error={state.error} />
        <Button onClick={() => window.location.reload()}>Попробовать снова</Button>
      </main>
    );
  }

  /*
   * SDK Telegram на странице нет вовсе. В разработке это обычный браузер, и
   * войти можно тестовым токеном. В проде показывать форму для токена нельзя:
   * человек оказывается здесь не потому, что он разработчик, а потому что
   * открыл ссылку в браузере или у него не загрузился скрипт Telegram.
   */
  if (import.meta.env.DEV) {
    return (
      <LocalLogin
        initialError={state.error}
        onReady={(user) => setState({ kind: 'ready', user })}
      />
    );
  }

  return (
    <main className="center-page">
      <div className="brand-mark">AF</div>
      <h1>Откройте приложение в Telegram</h1>
      <p className="muted">
        Приложение работает внутри Telegram: в обычном браузере входить нечем. Откройте чат с ботом
        и нажмите кнопку меню рядом с полем ввода или отправьте ему /app.
      </p>
      <p className="muted">
        Если вы открыли его именно из Telegram, значит часть приложения не загрузилась. Обновите
        страницу, а если не поможет — попробуйте другую сеть: мобильный интернет вместо Wi-Fi или
        наоборот.
      </p>
      <Button onClick={() => window.location.reload()}>Обновить</Button>
    </main>
  );
}

function LocalLogin({
  initialError,
  onReady,
}: {
  initialError?: unknown;
  onReady: (user: User) => void;
}) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<unknown>(initialError);
  const [loading, setLoading] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setLoading(true);
    setError(undefined);
    setAccessToken(token.trim());
    try {
      const session = await api<{ user: User }>('/auth/session');
      onReady(session.user);
    } catch (nextError) {
      setAccessToken(null);
      setError(nextError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="center-page">
      <div className="brand-mark">AF</div>
      <p className="eyebrow">Режим разработки</p>
      <h1>Откройте Mini App в Telegram</h1>
      <p className="muted">
        В браузере можно войти тестовым токеном. Получите его командой{' '}
        <code>npm run dev:artist -- token</code>.
      </p>
      <form className="stack" onSubmit={(event) => void submit(event)}>
        <Input
          value={token}
          onChange={(event) => setToken(event.target.value)}
          placeholder="Bearer-токен"
          autoComplete="off"
        />
        {error !== undefined && <ErrorNotice error={error} />}
        <Button type="submit" wide disabled={!token.trim() || loading}>
          {loading ? 'Проверяем…' : 'Войти'}
        </Button>
      </form>
    </main>
  );
}
